import { Box, Button, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";

function isAdminOnlyError(reason) {
  return /admin|not authorized|access required/i.test(String(reason || ""));
}

function isAccountUnavailableError(reason) {
  return /inactive|appears to have been deleted|no longer available|school operator|غیر فعال|حذف شدہ|اسکول آپریٹر/i.test(
    String(reason || ""),
  );
}

function isAccountDeletedError(reason) {
  return /appears to have been deleted|no longer available|حذف شدہ/i.test(
    String(reason || ""),
  );
}

/**
 * Access-denied / session failure shell for authenticated users who cannot
 * open the admin workspace. Matches xs-learnix-admin-app DashboardSessionError.
 */
export default function DashboardSessionError({
  reason,
  onRetry,
  retrying = false,
}) {
  const theme = useTheme();
  const accountUnavailable = isAccountUnavailableError(reason);
  // Prefer account-unavailable over admin-only when both patterns match.
  const adminOnly = !accountUnavailable && isAdminOnlyError(reason);
  const accountDeleted = accountUnavailable && isAccountDeletedError(reason);
  const nonRetryable = adminOnly || accountUnavailable;
  const primary = theme.palette.primary.main;
  const isDark = theme.palette.mode === "dark";
  const paperBg = alpha(theme.palette.background.paper, isDark ? 0.72 : 0.92);

  const title = adminOnly
    ? "Admin access only"
    : accountUnavailable
      ? accountDeleted
        ? "Account unavailable"
        : "Account inactive"
      : "Could not open your dashboard";
  const description = adminOnly
    ? "Only Website admins can access this console. You are signed in, but this XS identity is not authorized for the admin portal."
    : accountUnavailable
      ? accountDeleted
        ? "You can't use this account because it appears to have been deleted. Please contact your school operator."
        : "You can't use this account because it is inactive. Please contact your school operator."
      : reason?.trim() ||
        "We could not load your admin workspace. Try again, or use the account menu above to switch identity.";
  const icon = nonRetryable
    ? "mdi:shield-account-outline"
    : "mdi:alert-circle-outline";
  const iconBg = nonRetryable
    ? alpha(primary, 0.14)
    : alpha(theme.palette.warning.main, 0.16);
  const iconColor = nonRetryable ? primary : theme.palette.warning.main;

  return (
    <div className="session-error-content">
      <Box
        role="alert"
        aria-live="polite"
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 520,
          mx: "auto",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          background: paperBg,
          backdropFilter: "blur(14px)",
          border: `1px solid ${alpha(primary, isDark ? 0.3 : 0.18)}`,
          boxShadow: isDark
            ? `0 24px 64px ${alpha("#000", 0.45)}`
            : `0 20px 48px ${alpha("#000", 0.08)}`,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 72,
            height: 72,
            borderRadius: "50%",
            mb: 2,
            background: iconBg,
            color: iconColor,
          }}
        >
          <Icon icon={icon} width={40} height={40} />
        </Box>

        <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
          {title}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: 400,
            mx: "auto",
            lineHeight: 1.6,
            mb: nonRetryable ? 2 : 3,
          }}
        >
          {description}
        </Typography>

        {nonRetryable ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 400, mx: "auto", lineHeight: 1.55 }}
          >
            Use Switch account in the menu above to pick a different XS
            identity, or sign out from there if needed.
          </Typography>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => void onRetry?.()}
            disabled={retrying}
            sx={{ minWidth: 140, textTransform: "none", fontWeight: 600 }}
          >
            {retrying ? "Please wait…" : "Try again"}
          </Button>
        )}
      </Box>
    </div>
  );
}
